import { useMemo } from 'react';
import { PermissionDto, PermissionAction } from '@sib/api';
import { useUserSetupStore } from '@sib/auth';

const actionHierarchy: Record<PermissionAction, PermissionAction[]> = {
  [PermissionAction.All]: [
    PermissionAction.Read,
    PermissionAction.Write,
    PermissionAction.Delete,
    PermissionAction.All,
  ],
  [PermissionAction.Delete]: [PermissionAction.Read, PermissionAction.Delete],
  [PermissionAction.Write]: [PermissionAction.Read, PermissionAction.Write],
  [PermissionAction.Read]: [PermissionAction.Read],
};

const checkIfIncludesAction = (
  userAction: PermissionAction,
  requiredAction: PermissionAction
): boolean => actionHierarchy[userAction]?.includes(requiredAction) ?? false;

export const useCan = (permissions: PermissionDto[] | PermissionDto = []) => {
  const userPermissions = useUserSetupStore(state => state.userPermissions);

  const permissionsArray = useMemo(
    () => (Array.isArray(permissions) ? permissions : [permissions]),
    [permissions]
  );

  const isPermissionGranted = useMemo(
    () =>
      permissionsArray.every(requiredPermission => {
        const hasProperPermission = userPermissions.some(userPermission => {
          if (!userPermission.action || !userPermission.resource) return false;

          const isProperResource =
            userPermission.resource === requiredPermission.resource;

          if (!isProperResource) return false;

          const hasActionPermission = checkIfIncludesAction(
            userPermission.action,
            requiredPermission.action as PermissionAction
          );

          return hasActionPermission;
        });

        return hasProperPermission;
      }),
    [permissionsArray, userPermissions]
  );

  return isPermissionGranted;
};
